import { Formik, FormikHelpers } from 'formik';
import React, { useMemo } from 'react';

import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';
import AvatarComponent from 'components/common/avatar/AvatarComponent';
import useEmployeeDebounceOptions from 'hooks/useEmployeeDebounceOptions';
import FormDropdown from 'components/common/inputWrapper/components/Select';
import FormDateSelect from 'components/common/inputWrapper/components/DateSelect';
import DebounceFormDropdown from 'components/common/debounceFormDropdown/DebounceFormDropdown';

import {
  useDepartmentsQuery,
  useDesignationAreasQuery,
  useDesignationsQuery,
  useEngagementStatusesQuery,
} from 'hooks/query/useLookupQueries';
import { useActivateUserMutation } from 'hooks/query/useUserQueries';

import { TERMINATED } from 'constants/employeeStatus';
import activateEmployeeSchema from 'schemas/activateEmployeeSchema';

import { success } from 'utils/toast';
import { getFormattedDate } from 'utils/date';
import { extractFullName } from 'utils/string';

import { ActiveEmployee } from 'interface/employee';
import { EmployeeLike } from 'interface/userUtils';
import { ServicePayload } from 'interface/service';

interface ActivationFormValues extends Omit<ActiveEmployee, 'transitionDate'> {
  transitionDate: string;
  leaveIssuerId: number | null;
  areaId: number | null;
  designationId: number | null;
  departmentId: number | null;
  appraiserId: number | null;
  coAppraiserId: number | null;
  engagementStatusId: number | null;
}

interface ActivationFormModalProps {
  employee: EmployeeLike;
  afterSubmit?: (employeeData: EmployeeLike) => void;
}

type DropdownOption = {
  value: string | number;
  label?: string;
  empId?: string;
};

type DebounceOptions = [boolean, DropdownOption[], string, (code: string) => void];

interface TriggerButtonProps {
  onClick: () => void;
}

const ActivateLeapfroggerTriggerBtn: React.FC<TriggerButtonProps> = ({ onClick }) => (
  <button onClick={onClick} className="btn btn--outlined-primary order-1 momt-10">
    Activate Leapfrogger
  </button>
);

const ActivationFormModal: React.FC<ActivationFormModalProps> = ({ employee, afterSubmit }) => {
  const activateUserMutation = useActivateUserMutation();
  const { data: departments = [] } = useDepartmentsQuery();
  const { data: designations = [], isLoading: isDesignationsLoading } = useDesignationsQuery();
  const { data: areas = [], isLoading: isAreasLoading } = useDesignationAreasQuery();
  const { data: engagementStatus = [], isLoading: isEngagementStatusLoading } = useEngagementStatusesQuery();

  const leaveIssuerDebounceOptions = useEmployeeDebounceOptions() as DebounceOptions;
  const appraiserDebounceOptions = useEmployeeDebounceOptions() as DebounceOptions;
  const coAppraiserDebounceOptions = useEmployeeDebounceOptions() as DebounceOptions;

  const initialValues: ActivationFormValues = {
    leaveIssuerId: null,
    areaId: null,
    designationId: null,
    departmentId: null,
    appraiserId: null,
    coAppraiserId: null,
    engagementStatusId: null,
    transitionDate: getFormattedDate(),
  };

  const designationOptions = useMemo(
    () => designations.map((designation) => ({ label: designation.name, value: designation.id })),
    [designations]
  );

  const areaOptions = useMemo(() => areas.map((area) => ({ label: area.name, value: area.id })), [areas]);

  const departmentOptions = useMemo(
    () => departments.map((department) => ({ label: department.name, value: department.id })),
    [departments]
  );

  const engagementStatusOptions = useMemo(
    () =>
      engagementStatus
        .filter(({ name }) => name !== TERMINATED)
        .map((status) => ({ label: status.name, value: status.id })),
    [engagementStatus]
  );

  const handleSubmit = async (values: ActivationFormValues, actions: FormikHelpers<ActivationFormValues>) => {
    const payload: ServicePayload = { ...values };

    try {
      const { data } = (await activateUserMutation.mutateAsync({ id: employee.id, payload })) as {
        data: EmployeeLike;
      };

      success({ title: 'Success', message: 'Employee activated successfully' });

      if (afterSubmit) {
        afterSubmit(data);
      }
    } catch {
      // handleMutationError in useActivateUserMutation already surfaces the error toast
    } finally {
      actions.setSubmitting(false);
    }
  };

  if (!afterSubmit) {
    return null;
  }

  return (
    <LFModal
      closeButton={true}
      interactive={true}
      htmlContent={
        <Formik<ActivationFormValues>
          validationSchema={activateEmployeeSchema}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          render={({ values, touched, handleSubmit, handleChange, handleBlur, errors, isSubmitting }) => (
            <form>
              <div className="office-information-form__full-width">
                <div className="office-information-form__full-width__space">
                  <FormDropdown
                    value={values.designationId}
                    name="designationId"
                    label="Designation"
                    isMandatory
                    error={touched.designationId && errors.designationId}
                    options={designationOptions}
                    isLoading={isDesignationsLoading}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="office-information-form__full-width__space">
                  <FormDropdown
                    name="areaId"
                    label="Area"
                    isMandatory
                    error={touched.areaId && errors.areaId}
                    options={areaOptions}
                    isLoading={isAreasLoading}
                    value={values.areaId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              </div>
              <div className="office-information-form__full-width">
                <div className="office-information-form__full-width__space">
                  <FormDropdown
                    name="engagementStatusId"
                    label="Employment Status"
                    isMandatory
                    classNamePrefix="lf-select"
                    error={touched.engagementStatusId && errors.engagementStatusId}
                    options={engagementStatusOptions}
                    value={values.engagementStatusId}
                    isLoading={isEngagementStatusLoading}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="office-information-form__full-width__space">
                  <FormDateSelect
                    name="transitionDate"
                    label="Transition Date"
                    isMandatory
                    error={touched.transitionDate && errors.transitionDate}
                    value={values.transitionDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              </div>
              <FormDropdown
                name="departmentId"
                label="Department"
                isMandatory
                isClearable
                error={touched.departmentId && errors.departmentId}
                options={departmentOptions}
                value={values.departmentId}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <DebounceFormDropdown
                name="leaveIssuerId"
                label="Leave Issuer"
                isMandatory
                isClearable
                error={touched.leaveIssuerId && errors.leaveIssuerId}
                value={values.leaveIssuerId}
                debounceOptions={leaveIssuerDebounceOptions}
                iconComponent={(option) => (
                  <AvatarComponent label={String(option.label ?? '')} value={option as { empId?: string | number }} />
                )}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
              <DebounceFormDropdown
                name="appraiserId"
                label="Appraiser"
                error={touched.appraiserId && errors.appraiserId}
                isMandatory
                isClearable
                debounceOptions={appraiserDebounceOptions}
                iconComponent={(option) => (
                  <AvatarComponent label={String(option.label ?? '')} value={option as { empId?: string | number }} />
                )}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
              <DebounceFormDropdown
                name="coAppraiserId"
                label="Co-Appraiser"
                isClearable
                error={touched.coAppraiserId && errors.coAppraiserId}
                debounceOptions={coAppraiserDebounceOptions}
                iconComponent={(option) => (
                  <AvatarComponent label={String(option.label ?? '')} value={option as { empId?: string | number }} />
                )}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
              <div className="d-flex">
                <button
                  type="submit"
                  className="btn btn--primary margin-left-auto p-relative"
                  disabled={isSubmitting}
                  onClick={() => handleSubmit()}
                >
                  Activate
                  {isSubmitting && <Loading />}
                </button>
              </div>
            </form>
          )}
        />
      }
      title={{
        text: `Activate ${extractFullName(
          {
            firstName: employee.firstName || '',
            middleName: employee.middleName || '',
            lastName: employee.lastName || '',
          },
          true
        )}`,
        type: 'info',
      }}
      TriggerBtn={ActivateLeapfroggerTriggerBtn}
    />
  );
};

export default ActivationFormModal;
